import { useState, useEffect } from "react";
import { MapPin, Navigation, Clock, Phone, Star, CheckCircle } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const MECHANIC = {
  name: "Marcus J.",
  rating: 4.97,
  jobs: 1284,
  eta: 18,
  vehicle: "White Toyota Hilux",
  plate: "CA 482-173",
  avatar: "MJ",
};

const ROUTE_PROGRESS = [
  { label: "Dispatched", done: true },
  { label: "En Route", done: true },
  { label: "Arriving Soon", done: false },
  { label: "On-Site", done: false },
];

export default function MechanicTracker() {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [eta, setEta] = useState(MECHANIC.eta);
  const [mechX, setMechX] = useState(60);
  const [mechY, setMechY] = useState(160);
  const [progress, setProgress] = useState(0.1);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((p) => {
        const next = Math.min(p + 0.005, 0.95);
        // Animate mechanic along path
        const t = next;
        // Cubic bezier approximation: M60,160 Q160,100 240,90 Q300,80 340,40
        const x = 60 + (340 - 60) * t;
        const y = 160 - (120) * Math.sin(t * Math.PI * 0.8);
        setMechX(x);
        setMechY(y);
        return next;
      });
      setEta((e) => Math.max(0, e - 0.08));
    }, 200);
    return () => clearInterval(interval);
  }, []);

  const etaDisplay = Math.ceil(eta);

  return (
    <section
      id="tracking"
      className="py-20 sm:py-24 px-5 sm:px-6 md:px-12 lg:px-20 relative overflow-hidden"
    >
      {/* Subtle phone image bg */}
      <div
        className="absolute right-0 top-0 bottom-0 w-1/3 bg-cover bg-center opacity-5 hidden xl:block"
        style={{
          backgroundImage:
            "url(/tracker-bg.webp)",
        }}
      />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="mb-12">
          <p className={`text-xs uppercase tracking-[0.2em] mb-3 font-mono font-semibold ${isDark ? "text-[#00F0FF]" : "text-brand-teal"}`}>
            Live simulation
          </p>
          <h2 className={`font-display font-bold text-4xl lg:text-5xl tracking-tighter ${isDark ? "text-white" : "text-slate-900"}`}>
            Mechanic Tracking
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Map */}
          <div className="lg:col-span-2 rounded-2xl overflow-hidden border border-[#222226] relative bg-[#0A0A0D]" style={{ minHeight: 360 }}>
            {/* Grid pattern */}
            <div className="absolute inset-0 grid-pattern opacity-50" />

            {/* Scan line */}
            <div
              className="absolute left-0 right-0 h-px scan-line"
              style={{ background: "linear-gradient(90deg, transparent, rgba(0,240,255,0.4), transparent)" }}
            />

            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 600 360" preserveAspectRatio="xMidYMid meet">
              {/* Faint road grid */}
              {[60, 120, 180, 240, 300].map((y) => (
                <line key={`h${y}`} x1="0" y1={y} x2="600" y2={y} stroke="#ffffff" strokeOpacity="0.03" strokeWidth="1" />
              ))}
              {[100, 200, 300, 400, 500].map((x) => (
                <line key={`v${x}`} x1={x} y1="0" x2={x} y2="360" stroke="#ffffff" strokeOpacity="0.03" strokeWidth="1" />
              ))}

              {/* Main road */}
              <path d="M 80 300 Q 200 200 300 180 Q 400 160 520 80" stroke="#ffffff" strokeOpacity="0.06" strokeWidth="14" fill="none" strokeLinecap="round" />

              {/* Completed route */}
              <path
                d={`M 80 300 Q ${80 + (220) * progress * 0.8} ${300 - 120 * progress} ${mechX} ${mechY}`}
                stroke="#00F0FF"
                strokeWidth="3"
                fill="none"
                strokeOpacity="0.8"
                strokeLinecap="round"
              />

              {/* Remaining route dashed */}
              <path
                d={`M ${mechX} ${mechY} Q ${mechX + 100} ${mechY - 40} 520 80`}
                stroke="#00F0FF"
                strokeWidth="1.5"
                fill="none"
                strokeOpacity="0.3"
                strokeDasharray="6 8"
              />

              {/* Destination pulse */}
              <circle cx="520" cy="80" r="20" fill="#00F0FF" opacity="0.06">
                <animate attributeName="r" values="16;26;16" dur="2.5s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.08;0.02;0.08" dur="2.5s" repeatCount="indefinite" />
              </circle>
              <circle cx="520" cy="80" r="8" fill="#00F0FF" opacity="0.9" />
              <text x="530" y="72" fill="#ffffff" fontSize="11" opacity="0.7" fontFamily="JetBrains Mono">You</text>

              {/* Mechanic dot */}
              <circle cx={mechX} cy={mechY} r="14" fill="#00F0FF" opacity="0.15" />
              <circle cx={mechX} cy={mechY} r="8" fill="#22E584" />
              <circle cx={mechX} cy={mechY} r="8" fill="none" stroke="#22E584" strokeWidth="2" opacity="0.5">
                <animate attributeName="r" values="8;18;8" dur="2s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.5;0;0.5" dur="2s" repeatCount="indefinite" />
              </circle>

              {/* Direction arrow */}
              <text
                x={mechX + 12}
                y={mechY - 10}
                fill="#22E584"
                fontSize="10"
                fontFamily="JetBrains Mono"
                opacity="0.9"
              >
                ▲
              </text>
            </svg>

            {/* Floating ETA card */}
            <div className="absolute top-4 left-4 glass rounded-xl px-4 py-3 flex items-center gap-3">
              <div className="w-2 h-2 rounded-full bg-[#22E584] pulse-dot" />
              <div>
                <p className="text-[10px] text-[#8F9098] uppercase tracking-wider">
                  Mechanic ETA
                </p>
                <p className={`text-xl font-display font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                  {etaDisplay} <span className="text-sm font-normal text-[#8F9098]">min</span>
                </p>
              </div>
            </div>

            {/* Route progress */}
            <div className="absolute bottom-4 left-4 right-4">
              <div className="glass rounded-xl px-4 py-3">
                <div className="flex items-center justify-between">
                  {ROUTE_PROGRESS.map((step, i) => {
                    const isActive = !step.done && (i === 0 || ROUTE_PROGRESS[i - 1].done);
                    return (
                      <div key={step.label} className="flex items-center gap-2">
                        <div className="flex flex-col items-center">
                          <div
                            className={`w-2 h-2 rounded-full ${
                              step.done
                                ? "bg-[#22E584]"
                                : isActive
                                ? "bg-[#00F0FF] animate-pulse"
                                : "bg-[#222226]"
                            }`}
                          />
                          <span
                            className={`text-[9px] mt-1 whitespace-nowrap ${
                              step.done
                                ? "text-[#22E584]"
                                : isActive
                                ? "text-[#00F0FF]"
                                : "text-[#8F9098]/50"
                            }`}
                          >
                            {step.label}
                          </span>
                        </div>
                        {i < ROUTE_PROGRESS.length - 1 && (
                          <div
                            className={`w-10 sm:w-16 h-px ${
                              step.done ? "bg-[#22E584]/40" : "bg-[#222226]"
                            }`}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Mechanic info card */}
          <div className="flex flex-col gap-4">
            <div className={`rounded-2xl border p-5 ${isDark ? "bg-[#141419] border-[#222226]" : "bg-white border-slate-200 shadow-sm"}`}>
              <p className="text-xs text-[#8F9098] uppercase tracking-wider mb-4">
                Your Mechanic
              </p>
              <div className="flex items-center gap-4 mb-5">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#00F0FF]/20 to-[#0055FF]/20 border border-[#00F0FF]/20 flex items-center justify-center">
                  <span className={`font-display font-bold text-lg ${isDark ? "text-white" : "text-slate-900"}`}>
                    {MECHANIC.avatar}
                  </span>
                </div>
                <div>
                  <h3 className={`font-display font-semibold text-base ${isDark ? "text-white" : "text-slate-900"}`}>
                    {MECHANIC.name}
                  </h3>
                  <div className="flex items-center gap-1 mt-0.5">
                    <Star size={11} className="text-yellow-400 fill-yellow-400" />
                    <span className="text-xs text-[#8F9098]">
                      {MECHANIC.rating} · {MECHANIC.jobs.toLocaleString()} jobs
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-3 mb-5">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-[#8F9098]">Vehicle</span>
                  <span className={`text-xs ${isDark ? "text-white" : "text-slate-900"}`}>{MECHANIC.vehicle}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-[#8F9098]">Plate</span>
                  <span className="font-mono text-xs text-[#00F0FF]">{MECHANIC.plate}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-[#8F9098]">ETA</span>
                  <span className={`text-xs font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>{etaDisplay} min</span>
                </div>
              </div>

              <button className={`haptic w-full flex items-center justify-center gap-2 py-3 rounded-xl border text-sm transition-all ${isDark ? "border-[#222226] text-[#8F9098] hover:border-white/30 hover:text-white" : "border-slate-200 text-slate-600 hover:border-brand-blue/30 hover:text-brand-blue"}`}>
                <Phone size={13} />
                Contact Mechanic
              </button>
            </div>

            {/* Status card */}
            <div className={`rounded-2xl border p-5 ${isDark ? "bg-[#00F0FF]/5 border-[#00F0FF]/20" : "bg-brand-teal/5 border-brand-teal/20"}`}>
              <div className="flex items-center gap-2 mb-2">
                <Navigation size={14} className={isDark ? "text-[#00F0FF]" : "text-brand-teal"} />
                <span className={`text-xs font-semibold uppercase tracking-wide ${isDark ? "text-[#00F0FF]" : "text-brand-teal"}`}>
                  En Route
                </span>
              </div>
              <p className="text-sm text-[#8F9098] leading-relaxed">
                Marcus is heading your way. You'll get a notification when he's 5 min out.
              </p>
              <div className={`mt-3 rounded-lg overflow-hidden h-1.5 ${isDark ? "bg-[#0C0C0F]" : "bg-slate-200"}`}>
                <div
                  className={`h-full transition-all duration-300 rounded-lg ${isDark ? "bg-[#00F0FF]" : "bg-brand-teal"}`}
                  style={{ width: `${progress * 100}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
